import React, { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Loader2, Lock, CheckCircle, AlertTriangle, CreditCard } from 'lucide-react';
import {
  getPayPalClientId,
  getPayPalMode,
  isPayPalConfigured,
  createPayPalOrderForButtons,
  onPayPalApprove
} from '../firebase/paypal';
import { useToast } from './Toast';

/**
 * PayPal Checkout Form Component
 * Renders PayPal button for payment
 */
function PayPalCheckoutForm({ items, customerEmail, customerName, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { showToast } = useToast();

  const total = items.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);

  const handleCreateOrder = async () => {
    setError(null);
    try {
      const paypalOrderId = await createPayPalOrderForButtons(items, customerEmail, customerName);
      return paypalOrderId;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const handleApprove = async (data, actions) => {
    setLoading(true);
    try {
      const result = await onPayPalApprove(data, actions);

      if (result.success) {
        // Digital product delivery and course enrollment is handled server-side
        // by the capturePayPalOrder Cloud Function

        showToast('Payment successful!', 'success');

        // Clear customer info from sessionStorage
        sessionStorage.removeItem('checkoutCustomerEmail');
        sessionStorage.removeItem('checkoutCustomerName');

        // Navigate to success page
        window.location.href = `/checkout/success?order_id=${result.orderId}`;
      } else {
        setError(result.error || 'Payment capture failed');
        setLoading(false);
      }
    } catch (err) {
      setError(err.message || 'Payment failed');
      setLoading(false);
    }
  };

  const handleError = (err) => {
    console.error('PayPal error:', err);
    setError('Payment failed. Please try again.');
    setLoading(false);
  };

  const handleCancel = () => {
    showToast('Payment cancelled', 'warning');
    // Clear pending order
    sessionStorage.removeItem('pendingOrderId');
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Order Summary</h3>
        <div className="space-y-3 mb-4">
          {items.map((item, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {item.thumbnail && (
                  <img src={item.thumbnail} alt={item.title} className="w-12 h-12 rounded object-cover" />
                )}
                <div>
                  <p className="font-medium text-gray-800">{item.title}</p>
                  {/* Display booking date/time if present */}
                  {item.bookingDate && item.bookingTime && (
                    <p className="text-sm text-[#c5a059] font-medium">
                      📅 {item.bookingDisplayDate || new Date(item.bookingDate).toLocaleDateString()} at {item.bookingTime}
                    </p>
                  )}
                  <p className="text-sm text-gray-500">Quantity: {item.quantity || 1}</p>
                </div>
              </div>
              <p className="font-semibold text-gray-800">£{(item.price * (item.quantity || 1)).toFixed(2)}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-gray-800">Total</span>
            <span className="text-2xl font-bold text-[#c5a059]">£{total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 text-[#c5a059] animate-spin mr-3" />
          <span className="text-gray-600">Processing payment...</span>
        </div>
      ) : (
        <div className="space-y-4">
          {/* PayPal Button */}
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
            <PayPalButtons
              style={{
                layout: 'vertical',
                color: 'gold',
                shape: 'rect',
                label: 'paypal',
                height: 50
              }}
              createOrder={handleCreateOrder}
              onApprove={handleApprove}
              onError={handleError}
              onCancel={handleCancel}
              disabled={loading}
            />
          </div>

          {/* Cancel Button */}
          <button
            type="button"
            onClick={onCancel}
            className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
          >
            Cancel
          </button>
        </div>
      )}

      <p className="text-xs text-gray-500 text-center">
        <Lock size={12} className="inline mr-1" />
        Secure payment powered by PayPal
      </p>
    </div>
  );
}

/**
 * Main Checkout Component
 * PayPal is the default and primary payment method
 */
export function Checkout({ items = [], onSuccess, onCancel }) {
  // Get customer info from sessionStorage if available (from funnels)
  const getInitialEmail = () => {
    return sessionStorage.getItem('checkoutCustomerEmail') || '';
  };
  const getInitialName = () => {
    return sessionStorage.getItem('checkoutCustomerName') || '';
  };

  const [customerEmail, setCustomerEmail] = useState(getInitialEmail());
  const [customerName, setCustomerName] = useState(getInitialName());
  const [step, setStep] = useState(getInitialEmail() && getInitialName() ? 'payment' : 'details'); // Skip details if info is pre-filled

  const paypalClientId = getPayPalClientId();
  const paypalConfigured = isPayPalConfigured();

  const handleContinue = () => {
    if (!customerEmail || !customerName) {
      alert('Please fill in all fields');
      return;
    }
    // Basic email validation
    if (!customerEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }
    setStep('payment');
  };

  const total = items.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 text-center">
          <p className="text-gray-600 mb-4">Your cart is empty</p>
          <button
            onClick={onCancel}
            className="px-6 py-2 bg-[#c5a059] text-white rounded-lg hover:bg-[#b08e4d] transition"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-serif text-gray-800 mb-2">Checkout</h1>
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'details' ? 'bg-[#c5a059] text-white' : 'bg-green-500 text-white'
            }`}>
            {step === 'details' ? '1' : <CheckCircle size={16} />}
          </div>
          <div className="flex-1 h-1 bg-gray-200">
            <div className={`h-full ${step === 'payment' ? 'bg-[#c5a059]' : 'bg-gray-200'}`} style={{ width: step === 'payment' ? '100%' : '0%' }} />
          </div>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'payment' ? 'bg-[#c5a059] text-white' : 'bg-gray-200 text-gray-500'
            }`}>
            2
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="lg:col-span-2">
          {step === 'details' ? (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 sm:mb-6">Customer Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                    placeholder="john@example.com"
                    required
                  />
                </div>
                <button
                  onClick={handleContinue}
                  className="w-full px-6 py-3 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition"
                >
                  Continue to Payment
                </button>
              </div>
            </div>
          ) : (
            paypalConfigured ? (
              <PayPalScriptProvider options={{
                clientId: paypalClientId,
                currency: 'GBP',
                intent: 'capture'
              }}>
                <PayPalCheckoutForm
                  items={items}
                  customerEmail={customerEmail}
                  customerName={customerName}
                  onSuccess={onSuccess}
                  onCancel={() => setStep('details')}
                />
              </PayPalScriptProvider>
            ) : (
              <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 text-center">
                <AlertTriangle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">Payment Not Configured</h3>
                <p className="text-gray-600 mb-4">
                  PayPal payment integration is not set up. Please configure your PayPal Client ID in the environment variables.
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Add <code className="bg-gray-100 px-2 py-1 rounded">VITE_PAYPAL_CLIENT_ID</code> to your .env file.
                </p>
                <button
                  onClick={() => setStep('details')}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                >
                  Go Back
                </button>
              </div>
            )
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 lg:sticky lg:top-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Order Summary</h3>
            <div className="space-y-3 mb-4">
              {items.map((item, index) => (
                <div key={index} className="text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">{item.title} × {item.quantity || 1}</span>
                    <span className="font-medium text-gray-800">£{(item.price * (item.quantity || 1)).toFixed(2)}</span>
                  </div>
                  {item.bookingDate && item.bookingTime && (
                    <p className="text-xs text-[#c5a059] mt-1">
                      📅 {item.bookingDisplayDate || new Date(item.bookingDate).toLocaleDateString()} at {item.bookingTime}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium text-gray-800">£{total.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-gray-800">Total</span>
                <span className="text-xl font-bold text-[#c5a059]">£{total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Success Page Component
 * Shown after successful PayPal payment
 */
export function CheckoutSuccess() {
  const [funnelReturnInfo, setFunnelReturnInfo] = useState(null);
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    // Check for funnel return info in sessionStorage
    const stored = sessionStorage.getItem('funnelReturnInfo');
    if (stored) {
      try {
        const info = JSON.parse(stored);
        setFunnelReturnInfo(info);
      } catch (e) {
        console.error('Error parsing funnel return info:', e);
      }
    }
  }, []);

  useEffect(() => {
    // Auto-redirect for funnel flows
    if (funnelReturnInfo && funnelReturnInfo.hasNextStep && verified) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Redirect to funnel - FunnelPage will handle advancing to next step
            window.location.href = `/funnel/${funnelReturnInfo.slug}?paymentSuccess=true&stepIndex=${funnelReturnInfo.stepIndex}`;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [funnelReturnInfo, verified]);

  useEffect(() => {
    // Get params from URL
    const params = new URLSearchParams(window.location.search);
    const urlOrderId = params.get('order_id');

    setOrderId(urlOrderId);

    const verify = async () => {
      // For PayPal, payment is captured server-side before redirect
      // So if we're here with an order_id, payment was successful
      if (urlOrderId) {
        setVerified(true);

        // Digital product delivery is handled server-side by PayPal Cloud Functions
        // No need to call deliverDigitalProducts here
      }
      setVerifying(false);
    };
    verify();
  }, []);

  if (verifying) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <Loader2 className="w-12 h-12 text-[#c5a059] animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Verifying payment...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 text-center">
        {verified ? (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-green-600" size={32} />
            </div>
            <h1 className="text-2xl font-semibold text-gray-800 mb-2">Payment Successful!</h1>
            <p className="text-gray-600 mb-6">
              Thank you for your purchase. Your order has been confirmed.
            </p>
            {orderId && (
              <p className="text-sm text-gray-500 mb-6">
                Order ID: {orderId}
              </p>
            )}
            <p className="text-sm text-gray-600 mb-6">
              You will receive an email confirmation shortly with your order details and download links.
            </p>
            {funnelReturnInfo && funnelReturnInfo.hasNextStep ? (
              <div className="space-y-4">
                <p className="text-lg font-semibold text-gray-800">
                  Redirecting you to your next step in {countdown} seconds...
                </p>
                <a
                  href={`/funnel/${funnelReturnInfo.slug}?paymentSuccess=true&stepIndex=${funnelReturnInfo.stepIndex}`}
                  className="inline-block px-8 py-3 bg-[#c5a059] text-white rounded-lg hover:bg-[#b08e4d] transition font-semibold text-lg"
                >
                  Continue Now →
                </a>
              </div>
            ) : (
              <a
                href={funnelReturnInfo ? `/funnel/${funnelReturnInfo.slug}` : '/'}
                className="inline-block px-6 py-2 bg-[#c5a059] text-white rounded-lg hover:bg-[#b08e4d] transition"
              >
                {funnelReturnInfo ? 'Continue Funnel' : 'Return to Home'}
              </a>
            )}
          </>
        ) : (
          <>
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="text-yellow-600" size={32} />
            </div>
            <h1 className="text-2xl font-semibold text-gray-800 mb-2">Payment Verification</h1>
            <p className="text-gray-600 mb-6">
              We're verifying your payment. Please check your email for confirmation.
            </p>
            {funnelReturnInfo && funnelReturnInfo.hasNextStep ? (
              <a
                href={`/funnel/${funnelReturnInfo.slug}?paymentSuccess=true&stepIndex=${funnelReturnInfo.stepIndex}`}
                className="inline-block px-8 py-3 bg-[#c5a059] text-white rounded-lg hover:bg-[#b08e4d] transition font-semibold text-lg"
              >
                Continue to Next Step →
              </a>
            ) : (
              <a
                href={funnelReturnInfo ? `/funnel/${funnelReturnInfo.slug}` : '/'}
                className="inline-block px-6 py-2 bg-[#c5a059] text-white rounded-lg hover:bg-[#b08e4d] transition"
              >
                {funnelReturnInfo ? 'Continue Funnel' : 'Return to Home'}
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
}
